/**
 * Google Ads conversion tracking via Segment
 *
 * Segment's Google Ads (Conversions) destination needs:
 * - GCLID, GBRAID, or WBRAID for click attribution (captured from landing URL)
 * - value + currency for conversion value
 *
 * Capture runs on app load; conversion events attach the stored click ID.
 */

const STORAGE_KEY = 'segment_google_click_id';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

const getParams = () => {
  if (typeof window === 'undefined' || !window.location || !window.location.search) {
    return {};
  }
  const params = new URLSearchParams(window.location.search);
  const out = {
    gclid: params.get('gclid') || undefined,
    gbraid: params.get('gbraid') || undefined,
    wbraid: params.get('wbraid') || undefined,
  };
  UTM_KEYS.forEach(key => {
    const v = params.get(key);
    if (v) out[key] = v;
  });
  return out;
};

/**
 * Call on app load (e.g. when location changes) to store Google Click ID and UTM params from URL.
 * Sticky for the session so conversion events can send source/medium (avoids "Unassigned" in GA4).
 */
export const captureGoogleClickIdFromUrl = () => {
  const params = getParams();
  const hasAny = params.gclid || params.gbraid || params.wbraid || UTM_KEYS.some(k => params[k]);
  if (!hasAny) return;
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    const existingObj = existing ? JSON.parse(existing) : {};
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existingObj, ...params }));
  } catch (e) {
    // ignore storage errors
  }
};

/**
 * Returns an object with gclid, gbraid, wbraid (and optionally utm_*) to attach to Segment track().
 * Segment's Google Ads destination uses click IDs; GA4 uses UTM for source/medium attribution.
 */
export const getGoogleClickIdForConversion = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const out = {
      ...(parsed.gclid && { gclid: parsed.gclid }),
      ...(parsed.gbraid && { gbraid: parsed.gbraid }),
      ...(parsed.wbraid && { wbraid: parsed.wbraid }),
    };
    UTM_KEYS.forEach(key => {
      if (parsed[key]) out[key] = parsed[key];
    });
    return out;
  } catch (e) {
    return {};
  }
};
